import styled from 'styled-components'
import React, { useRef } from 'react'
import useOutsideClick from 'Hooks/useOutsideClick'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import { TRANSITION_ANIMATION_DURATION } from 'Lib/Constants'
import { useLockBodyScroll } from 'Lib'
import useModalBackButton from 'Hooks/useModalBackButton'

import { CloseButton } from './SharedStyles'

export const ModalSheetContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  padding-top: 30px;
  background-color: rgba(41, 41, 41, 0.4);
  z-index: 100;
  overflow: auto;

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    padding: 20px;
  }
`

export const ModalSheetContent = styled.div`
  width: calc(100% - ${({ theme }) => theme.spacing.mediumLarge} * 2);
  background-color: ${({ theme }) => theme.colors.basicWhite};
  border-radius: ${({ theme }) => theme.spacing.mediumLarge} ${({ theme }) => theme.spacing.mediumLarge} 0 0;
  padding: ${({ theme }) => theme.spacing.medium} ${({ theme }) => theme.spacing.mediumLarge};
  margin-top: auto;
  opacity: 1;
  transform: translateY(0%);

  @media (min-width: ${({ theme }) => theme.breakpoints.tablet}) {
    max-width: 420px;
    border-radius: ${({ theme }) => theme.spacing.mediumLarge};
    margin: auto;
  }
`

interface ModalSheetProps {
  show: boolean
  onClose: () => void
}

const ModalSheet: React.FC<React.PropsWithChildren<ModalSheetProps>> = ({ children, onClose, show }) => {
  const sheetRef = useRef<HTMLDivElement>(null)
  useModalBackButton(onClose, show)

  // Listens for clicks outside the modal (or on the overlay) to close it
  useOutsideClick(sheetRef, onClose)
  useLockBodyScroll(show)

  return (
    <TransitionGroup>
      <CSSTransition
        key={show ? 'modal-sheet-open' : 'modal-sheet-closed'}
        timeout={TRANSITION_ANIMATION_DURATION}
        classNames="modal-sheet-transition"
        unmountOnExit
      >
        {show
          ? (
            <ModalSheetContainer role="dialog" aria-modal="true">
              <ModalSheetContent ref={sheetRef}>
                <CloseButton onClick={onClose} aria-label="Close modal">&times;</CloseButton>
                {children}
              </ModalSheetContent>
            </ModalSheetContainer>
            )
          : (
            <></>
            )}
      </CSSTransition>
    </TransitionGroup>
  )
}

export default ModalSheet
